import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { inventoryApi, propertiesApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package } from 'lucide-react';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Link, useSearchParams } from 'react-router-dom';

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [properties, setProperties] = useState([]);
  const [params] = useSearchParams();
  const showLowOnly = params.get('low') === '1';

  const load = async () => {
    try {
      const [is, ps] = await Promise.all([inventoryApi.list({ low_only: showLowOnly }), propertiesApi.list()]);
      setItems(is); setProperties(ps);
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, [showLowOnly]);

  const adjust = async (id, delta) => { await inventoryApi.adjust(id, delta); load(); };

  return (
    <AppShell title="Inventory">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold">Inventory {showLowOnly && <span className="text-sm text-[hsl(var(--fc-warning))]">(Low stock)</span>}</h1>
        <p className="text-sm text-muted-foreground">All supplies across your properties.</p>
      </div>
      {!loaded ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState icon={Package} title="No inventory items" description="Add inventory items on each property to track supplies and alerts." />
      ) : (
        <Card><CardContent className="p-0"><div className="divide-y">
          {items.map(it => {
            const p = properties.find(x => x.id === it.property_id);
            const low = it.current_qty <= it.min_threshold;
            return (
              <div key={it.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                <div>
                  <div className="font-medium text-sm flex items-center gap-2"><Package className="h-4 w-4" />{it.name} {low && <Badge variant="outline" className="text-[10px] text-[hsl(var(--fc-warning))]">Low</Badge>}</div>
                  <div className="text-xs text-muted-foreground">{p ? <Link to={`/properties/${p.id}`} className="hover:underline">{p.nickname}</Link> : '—'} • Threshold: {it.min_threshold} {it.unit}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" onClick={() => adjust(it.id, -1)}>-</Button>
                  <div className="w-16 text-center tabular-nums font-semibold">{it.current_qty} {it.unit}</div>
                  <Button size="icon" variant="outline" onClick={() => adjust(it.id, 1)}>+</Button>
                </div>
              </div>
            );
          })}
        </div></CardContent></Card>
      )}
    </AppShell>
  );
}
